import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {connect} from 'react-redux';
import {Grid} from '@material-ui/core';
import {createForm, formShape} from 'rc-form';
import {isEmpty} from 'lodash/lang';
import moment from 'moment';
import {
  InputField,
  SelectField,
  AutoSuggestSelect,
  DateTimePickerField,
  WrapperField,
} from 'components/common/rc-fields';
import {GreatForm} from 'components/common';
import {fetchBooks} from 'api/books';
import {createAdminReading, updateAdminReading} from 'api/readings';
import {fetchUsers} from 'api/users';
import {fetchSchools} from 'api/schools';
import {fetchClassrooms} from 'api/classrooms';
import {buildUserFullName, getReadingReactions, getOptions} from 'utils/common';
import {fetchAllCommonImages} from '../../api/images';

const bookAdditionTypesMap = {
  select: 'Select a Book',
  add: 'Add a New Book',
};

const bookAdditionTypeOptions = getOptions(bookAdditionTypesMap);

const AdminReadingForm = ({form, user: {activeSchool}, initialValues, onCancel, onSubmitSuccess}) => {
  const isUpdate = !isEmpty(initialValues) && initialValues.id;
  const {dateCreated, book, comment, page, reaction, classroom} = initialValues;
  let {
    school: selectedSchool,
    classroom: selectedClassroom,
    bookAdditionType: selectBookAdditionType,
  } = form.getFieldsValue();
  selectedSchool = activeSchool || selectedSchool;
  const [reactionOptions, setReactionOptions] = useState([]);

  useEffect(() => {
    if (!isUpdate) return;
    form.setFieldsValue({bookAdditionType: 'select'});
  }, []);

  useEffect(() => {
    form.resetFields(['classroom', 'reader']);
  }, [selectedSchool]);

  useEffect(() => {
    form.resetFields(['reader']);
  }, [selectedClassroom]);

  useEffect(() => {
    let level;
    if (!isEmpty(selectedClassroom)) {
      level = selectedClassroom.level;
    } else if (!isEmpty(classroom)) {
      level = classroom.level;
    }
    if (!level) return;
    fetchAllCommonImages({type: 'reading-reactions', level}).then(({records}) => {
      setReactionOptions(getReadingReactions(records.filter(({active}) => active)));
    });
  }, [selectedClassroom, classroom]);

  return (
    <GreatForm
      form={form}
      onCancel={onCancel}
      submitFn={(payload) => {
        const {reaction} = payload;
        const {id: commonImageId} = reactionOptions[reaction / 2];
        return isUpdate
          ? updateAdminReading({id: initialValues.id, commonImageId, ...payload})
          : createAdminReading({commonImageId, ...payload});
      }}
      onSuccess={onSubmitSuccess}
      successMsg="Reading has been successfully created!"
    >
      <Grid container spacing={2}>
        {!isUpdate && (
          <React.Fragment>
            {isEmpty(activeSchool) && (
              <Grid item xs={12}>
                <AutoSuggestSelect
                  name="school"
                  label="School"
                  labelKey="name"
                  valueKey="id"
                  onSearch={(value) => fetchSchools(value).then(({records}) => records)}
                  rules={[{type: 'object', required: true, message: 'Please select a school'}]}
                  form={form}
                  autoComplete="off"
                />
              </Grid>
            )}
            {!isEmpty(selectedSchool) && (
              <Grid item xs={12}>
                <AutoSuggestSelect
                  name="classroom"
                  label="Classroom"
                  labelKey="name"
                  valueKey="id"
                  onSearch={(value) =>
                    fetchClassrooms(value, undefined, undefined, undefined, {schoolId: selectedSchool.id}).then(
                      ({records}) => records,
                    )
                  }
                  rules={[{type: 'object', required: true, message: 'Please select a classroom'}]}
                  form={form}
                  autoComplete="off"
                />
              </Grid>
            )}
            {!isEmpty(selectedClassroom) && (
              <Grid item xs={12}>
                <AutoSuggestSelect
                  name="reader"
                  label="Reader"
                  labelKey="name"
                  valueKey="id"
                  onSearch={(value) =>
                    fetchUsers(value, undefined, undefined, undefined, {
                      role: 'reader',
                      classroomId: selectedClassroom.id,
                    }).then(({records}) => records.map((record) => ({...record, name: buildUserFullName(record)})))
                  }
                  rules={[{type: 'object', required: true, message: 'Please select a book'}]}
                  form={form}
                  autoComplete="off"
                />
              </Grid>
            )}
          </React.Fragment>
        )}
        <Grid item xs={12}>
          <DateTimePickerField
            variant="static"
            orientation="landscape"
            name="date"
            label="Date"
            initialValue={!dateCreated ? undefined : moment(dateCreated)}
            rules={[{required: true, message: 'Please select a date'}]}
            form={form}
            autoComplete="off"
            withTime={false}
          />
        </Grid>
        <Grid item xs={12}>
          <SelectField
            name="bookAdditionType"
            label="Select or Create a Book"
            options={bookAdditionTypeOptions}
            rules={[{required: true, message: 'Please select one of the options'}]}
            form={form}
            autoComplete="off"
            renderValue={(value) => bookAdditionTypesMap[value] || ''}
          />
        </Grid>
        {selectBookAdditionType === 'select' ? (
          <Grid item xs={12}>
            <AutoSuggestSelect
              name="book"
              label="Select a Book"
              labelKey="title"
              valueKey="id"
              onSearch={(value) => fetchBooks(value).then(({records}) => records)}
              initialValue={isEmpty(book) ? null : book}
              rules={[{type: 'object', required: true, message: 'Please select a book'}]}
              form={form}
              autoComplete="off"
            />
          </Grid>
        ) : (
          selectBookAdditionType === 'add' && (
            <Grid item xs={12}>
              <InputField
                name="bookTitle"
                label="Enter Book Title"
                initialValue={isEmpty(book) ? '' : book.title}
                rules={[{required: true, message: 'Please enter book title'}]}
                form={form}
                autoComplete="off"
              />
            </Grid>
          )
        )}
        {!isEmpty(reactionOptions) && (
          <Grid item xs={12}>
            <WrapperField
              name="reaction"
              label="Reaction"
              initialValue={reaction}
              rules={[{required: true, message: 'Please select reaction'}]}
              form={form}
              render={({fieldProps: {value, onChange}}) => (
                <Grid container spacing={2} wrap="nowrap">
                  {reactionOptions.map(({value: reaction, image}) => {
                    const selected = value === reaction;
                    return (
                      <Grid
                        key={reaction}
                        item
                        xs={2}
                        onClick={() => onChange(reaction)}
                        className={clsx('clickable', 'has-effect', {active: selected})}
                      >
                        {image}
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <InputField name="page" label="Page" initialValue={!page ? '' : page.toString()} form={form} />
        </Grid>
        <Grid item xs={12}>
          <InputField
            name="comment"
            label="Comment"
            initialValue={isEmpty(comment) ? '' : comment}
            form={form}
            variant="outlined"
            multiline
            rows={8}
            rowsMax={32}
          />
        </Grid>
      </Grid>
    </GreatForm>
  );
};

AdminReadingForm.propTypes = {
  form: formShape.isRequired,
  user: PropTypes.object.isRequired,
  initialValues: PropTypes.object,
  onCancel: PropTypes.func,
  onSubmitSuccess: PropTypes.func.isRequired,
};

AdminReadingForm.defaultProps = {
  initialValues: {},
};

export default connect(
  ({account}) => ({
    user: account.get('user').toJS(),
  }),
  null,
)(createForm()(AdminReadingForm));
